// dynamic use hook props
import { defineAsyncComponent, unref } from 'vue'
import { Spin } from 'ant-design-vue'
import { isFunction } from './is'
import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router'
import type { App, Component, ComponentPublicInstance, Plugin, Slots, VNodeChild } from 'vue'

/**
 * 获取动态属性
 *
 * 该函数旨在从给定的属性对象中提取出一部分属性，这些属性的键和类型由泛型 U 定义。
 * 函数返回一个 Partial\<U\> 类型的对象，其中包含了原对象 T 中与 U 相对应的属性。
 * 这种方法通常用于在 Vue 组件中动态地将一些属性传递给子组件，而不需要传递所有的属性。
 *
 * @param props - 输入的属性对象，它是一个泛型 T 类型的对象，T 必须是对象类型。
 * @returns 返回一个 Partial\<U\> 类型的对象，其中包含了原对象 T 中与 U 相对应的属性。
 *
 * @source
 */
export function getDynamicProps<T extends object, U>(props: T): Partial<U> {
  // 初始化一个空对象，用于存放处理后的属性
  const ret: Record<string, any> = {}

  // 遍历输入对象 props 的所有键
  Object.keys(props).forEach((key) => {
    // 将 props 中对应键的值通过 unref 函数处理后，赋值给 ret 对象的相同键
    ret[key] = unref((props as Record<string, any>)[key])
  })

  // 将处理后的对象 ret 断言为 Partial<U> 类型后返回
  return ret as Partial<U>
}

/**
 * 获取原始路由信息。
 *
 * 该函数主要用于从给定的路由对象中提取并返回一个简化版的路由对象，尤其适用于需要对路由的matched属性进行特殊处理的场景。
 * 它通过复制路由对象的其他属性，并根据matched属性生成一个新的matched数组，从而确保返回的路由对象既包含必要的信息，又不会携带多余的数据。
 *
 * @param route 路由对象，包含路由的完整信息。
 * @returns 返回一个新的路由对象，该对象包含了与输入路由对象相同的所有属性，但matched属性被处理为只包含name、path和meta信息的简化版路由记录数组。
 *
 * @source
 */
export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  // 如果路由对象为空，则直接返回，避免不必要的处理。
  if (!route) return route

  // 解构路由对象，提取matched属性，并将其他属性收集到opt变量中。
  const { matched, ...opt } = route

  // 返回一个新的路由对象，该对象在保留了原路由对象所有属性的基础上，对matched属性进行了特殊处理。
  // 如果matched存在，则将其转换为只包含name、path和meta信息的路由记录数组；如果matched不存在，则将其设为undefined。
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  }
}

/**
 * 为组件添加安装功能，使其可以作为Vue插件安装。
 *
 * @param component 组件对象，需要添加安装方法。
 * @param alias 组件的别名，可选参数，用于全局注册组件时使用。
 * @returns 返回带有install方法的组件对象，类型为原组件类型和Plugin的合并类型。
 *
 * @example
 * // MyComponent.vue
 * export default withInstall(MyComponent)
 * export default withInstall(MyComponent, 'my-comp')
 *
 * // main.ts
 * const app = createApp(App)
 * app.use(MyComponent)
 *
 * // demo.vue
 * <template>
 *   <my-comp></my-comp>
 * </template>
 * @source
 */
/* istanbul ignore next -- @preserve */
export const withInstall = (component: Component, alias?: string): Plugin => {
  // 将组件强制转换为any类型，以方便后续添加install方法。
  const comp = component as any
  // 定义install方法，使组件可以被作为Vue插件安装。
  comp.install = (app: App) => {
    // 在应用实例中注册组件，使用组件的name或displayName作为标识。
    app.component(comp.name || comp.displayName, component)
    // 如果提供了别名，则将组件注册为全局属性，以便在模板中直接使用。
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }

  // 将组件对象按原组件类型和Plugin类型合并后返回，以便它可以被作为组件或插件使用。
  return component as Plugin
}

/**
 * 异步组件创建选项接口
 *
 * 该接口定义了创建异步组件时可用的一系列选项，用于自定义组件的加载行为和外观。
 */
export interface CreateAsyncComponentOptions {
  /**
   * 组件的尺寸
   *
   * 可选值为'default'、'small'、'large'，用于设定异步组件的尺寸样式。
   * 默认情况下，组件采用'default'尺寸。
   *
   * @default 'default'
   */
  size?: 'default' | 'small' | 'large'

  /**
   * 延迟加载时间
   *
   * 指定组件在多长时间后开始加载。单位为毫秒。
   * 用于实现懒加载效果，提高应用初始化速度。
   *
   * @default 100ms
   */
  delay?: number

  /**
   * 加载超时时间
   *
   * 指定组件加载的最长等待时间。单位为毫秒。
   * 如果在指定时间内组件未加载完成，则可能显示加载失败的提示。
   *
   * @default 30000ms
   */
  timeout?: number

  /**
   * 加载状态指示
   *
   * 用于控制是否显示加载状态指示器。
   * 当设置为true时，会在组件加载期间显示加载指示器。
   *
   * @default false
   */
  loading?: boolean

  /**
   * 加载失败后是否重试
   *
   * 当组件加载失败时，如果设置为true，则会尝试重新加载组件。
   * 这对于处理临时的加载问题非常有用，可以避免用户直接看到加载失败的状态。
   *
   * @default true
   */
  retry?: boolean
}

/**
 * 创建一个异步组件。
 *
 * 封装一个异步加载的组件，并支持自定义加载过程中的配置，如加载动画、重试逻辑等。
 *
 * @param loader 异步加载组件的函数，应返回一个Promise，Promise解析时应提供组件定义。
 * @param options 配置选项，包括加载动画大小、延迟加载时间、超时时间、是否显示加载动画及是否支持重试等。
 * @returns 返回一个定义了异步加载组件的Vue组件定义。
 *
 * @source
 */
/* istanbul ignore next -- @preserve */
export function createAsyncComponent(
  loader: () => Promise<any>,
  options: CreateAsyncComponentOptions = {},
): new () => ComponentPublicInstance {
  // 解构并默认设置异步组件的加载选项
  const {
    size = 'small', // 加载动画的大小
    delay = 100, // 延迟多久后显示加载动画
    timeout = 30000, // 加载组件的超时时间
    loading = false, // 是否显示加载动画
    retry = true, // 是否支持重试加载
  } = options

  // 使用Vue的defineAsyncComponent定义异步组件
  return defineAsyncComponent({
    loader, // 异步加载组件的函数
    loadingComponent: loading ? (
      <Spin spinning={true} size={size} /> // 当加载时显示的加载动画组件
    ) : undefined,
    timeout, // 加载组件的超时时间
    delay, // 延迟多久后显示加载动画
    onError: !retry
      ? () => {}
      : (error, retry, fail, attempts) => {
          console.log(error)
          // 错误处理逻辑，支持根据错误类型和重试次数决定是否重试
          if (error.message.match(/fetch/) && attempts <= 3) {
            retry()
          } else {
            fail()
          }
        },
  })
}

/**
 * 根据提供的插槽名称和数据，获取插槽函数的执行结果。
 * @param slots - 包含插槽函数的对象。
 * @param slot - 插槽的名称，默认为'default'。
 * @param data - 传递给插槽函数的数据，可选。
 * @returns {null | any} - 如果插槽不存在或不是函数，则返回null；否则返回插槽函数的执行结果。
 * @example
 * ```ts
 * getSlot({ default: () => 'default slot content' }, 'default')
 * // => 'default slot content'
 * ```
 * @source
 */
export function getSlot(slots: Slots, slot = 'default', data?: any): VNodeChild | null {
  // 检查slots对象是否存在且包含指定的插槽
  if (!slots || !Reflect.has(slots, slot)) {
    return null
  }
  // 检查插槽是否为函数，如果不是则打印错误并返回null
  if (!isFunction(slots[slot])) {
    console.error(`${slot} is not a function!`)
    return null
  }
  // 调用插槽函数并返回其结果
  return slots[slot]!(data)
}

/**
 * 扩展插槽对象，将指定插槽的引用添加到新对象中。
 *
 * 此函数的目的是为了创建一个包含原始插槽引用的新对象，但不包括在排除键列表中的插槽。
 * 这样可以在不直接修改原始插槽对象的情况下，创建一个定制的插槽引用对象。
 *
 * @param slots - 原始插槽对象，包含所有插槽的定义。
 * @param excludeKeys - 排除列表，一个字符串数组，指定不应该包含在新对象中的插槽键。
 * @returns - 返回一个新的对象，其中包含了原始插槽对象中除排除键之外的所有插槽的引用。
 * @example
 * ```ts
 * const extendedSlots = extendSlots({
 *   default: () => <div>Default Slot</div>,
 *   header: () => <div>Header Slot</div>,
 *   footer: () => <div>Footer Slot</div>,
 * }, ['header', 'footer']);
 * // => { default: () => <div>Default Slot</div> }
 * ```
 * @source
 */
export function extendSlots(slots: Slots, excludeKeys: string[] = []): Record<string, VNodeChild> {
  // 获取原始插槽对象的所有键
  const slotKeys = Object.keys(slots)
  // 初始化一个新的对象，用于存放扩展后的插槽引用
  const ret: any = {}

  // 遍历原始插槽对象的所有键
  slotKeys.forEach((key) => {
    // 检查当前键是否在排除列表中
    if (excludeKeys.includes(key)) {
      // 如果在排除列表中，则跳过当前插槽
      return null
    }
    // 如果不在排除列表中，则将当前插槽的引用添加到新对象中
    ret[key] = () => getSlot(slots, key)
  })

  // 返回扩展后的插槽引用对象
  return ret
}
